import { useNavigate } from "react-router-dom";
import {
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
  RotateCcw,
  ArrowRight,
  Tag,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { downloadErrorCsv } from "@/services/import";

interface SummaryStepProps {
  jobId: string;
  status: "completed" | "partial" | "failed";
  processedRows: number;
  successRows: number;
  errorRows: number;
  skippedRows: number;
  importedTags?: string[];
  fileName: string;
  onReset: () => void;
}

const STATUS_CONFIG = {
  completed: {
    icon: CheckCircle,
    label: "Import erfolgreich abgeschlossen",
    className: "border-green-200 bg-green-50 text-green-800",
    iconClassName: "text-green-600",
  },
  partial: {
    icon: AlertTriangle,
    label: "Import teilweise abgeschlossen",
    className: "border-yellow-200 bg-yellow-50 text-yellow-800",
    iconClassName: "text-yellow-600",
  },
  failed: {
    icon: XCircle,
    label: "Import fehlgeschlagen",
    className: "border-red-200 bg-red-50 text-red-800",
    iconClassName: "text-red-600",
  },
} as const;

function StatCard({
  label,
  count,
  color,
}: {
  label: string;
  count: number;
  color: "green" | "red" | "yellow" | "muted";
}) {
  const styles = {
    green: "border-green-200 bg-green-50 text-green-700",
    red: "border-red-200 bg-red-50 text-red-700",
    yellow: "border-yellow-200 bg-yellow-50 text-yellow-700",
    muted: "border-muted bg-muted/50 text-muted-foreground",
  };

  return (
    <div
      className={cn(
        "flex flex-col items-center rounded-lg border px-4 py-3",
        styles[color],
      )}
    >
      <p className="text-2xl font-bold">{count}</p>
      <p className="text-sm">{label}</p>
    </div>
  );
}

async function handleDownloadErrors(jobId: string) {
  const blob = await downloadErrorCsv(jobId);
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "import-errors.csv";
  a.click();
  URL.revokeObjectURL(url);
}

export function SummaryStep({
  jobId,
  status,
  processedRows,
  successRows,
  errorRows,
  skippedRows,
  importedTags,
  fileName,
  onReset,
}: SummaryStepProps) {
  const navigate = useNavigate();
  const config = STATUS_CONFIG[status];
  const StatusIcon = config.icon;

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      <div
        className={cn(
          "flex items-center gap-3 rounded-lg border px-5 py-4",
          config.className,
        )}
      >
        <StatusIcon className={cn("h-6 w-6 shrink-0", config.iconClassName)} />
        <div>
          <p className="font-semibold">{config.label}</p>
          <p className="text-sm opacity-80">{fileName}</p>
        </div>
      </div>

      {/* Result Cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Verarbeitet" count={processedRows} color="muted" />
        <StatCard label="Erfolgreich" count={successRows} color="green" />
        <StatCard label="Fehlerhaft" count={errorRows} color="red" />
        <StatCard label="Übersprungen" count={skippedRows} color="yellow" />
      </div>

      {/* Error CSV Download */}
      {(errorRows > 0 || skippedRows > 0) && (
        <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-5 py-4">
          <XCircle className="h-5 w-5 shrink-0 text-red-500" />
          <p className="flex-1 text-sm text-red-700">
            {errorRows + skippedRows} Zeile
            {errorRows + skippedRows > 1 ? "n" : ""} konnte
            {errorRows + skippedRows > 1 ? "n" : ""} nicht importiert werden.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleDownloadErrors(jobId)}
          >
            <Download className="mr-2 h-4 w-4" />
            Fehlerhafte Zeilen herunterladen (.csv)
          </Button>
        </div>
      )}

      {/* Imported Tags */}
      {importedTags && importedTags.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Tag className="h-4 w-4 text-blue-600" />
            <h3 className="text-sm font-semibold">Importierte Tags</h3>
          </div>
          <div className="flex flex-wrap gap-2">
            {importedTags.map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800"
              >
                {tag}
              </span>
            ))}
          </div>
          <p className="text-sm text-gray-500">
            Diese Tags können Sie jetzt im Scoring verwenden, um
            Produktempfehlungen zu konfigurieren.
          </p>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-2">
        <Button variant="outline" onClick={onReset}>
          <RotateCcw className="mr-2 h-4 w-4" />
          Neuen Import starten
        </Button>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => navigate("/products")}>
            Zur Produktübersicht
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
          {status !== "failed" && successRows > 0 && (
            <Button onClick={() => navigate("/quizzes")}>
              Weiter zu Scoring
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
